<template>
  <component
    :is="tag"
    v-show="nbHits > 0"
    ref="rootRef"
    class="san-infinite-grid"
    :style="rootStyles"
  >
    <component
      :is="probeTag"
      ref="probeRef"
      :style="{
        opacity: 0,
        visibility: 'hidden',
        gridArea: '1/1',
        pointerEvents: 'none',
        zIndex: -1,
        placeSelf: 'stretch',
      }"
    >
      <slot name="probe">
        <div
          class="san-infinite-grid--probe"
          :style="{ background: 'red' }"
        />
      </slot>
    </component>

    <template
      v-for="(item, i) in buffer"
      :key="getKey ? getKey(item) : keyPrefix + '.' + i"
    >
      <div
        class="san-infinite-grid--cell"
        :style="item.style"
      >
        <div class="san-infinite-grid--placeholder">
          <slot
            name="placeholder"
            :index="item.index"
            :style="item.style"
            :offset="item.offset"
            :is-highlighted="item.index === highlightedIndex"
          >
            <div
              class="san-infinite-grid--placeholder"
              :class="{ 'is-highlighted': item.isHighlighted }"
              :style="{
                ...item.style,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 'bold',
                color:'white',
                background:'red',
                borderRadius:'8px'
              }"
            >
              loading item {{ item.index }}...
            </div>
          </slot>
        </div>

        <div class="san-infinite-grid--item">
          <slot
            v-if="item.value"
            name="default"
            :item="item.value"
            :index="item.index"
            :style="item.style"
            :offset="item.offset"
            :is-highlighted="item.index === highlightedIndex"
          />
        </div>
      </div>
    </template>
  </component>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { scroll } from 'quasar'
import { getResizeMeasurement, computeSpaceBehindWindowOf, getScrollParents, useReactiveBuffer, getItemOffsetByIndex } from '@/helpers'
import { smoothScroll } from '@/helpers/scrollHelpers'

const emit = defineEmits(['reveal-pages', 'scroll-ended'])

const props = defineProps({
  pages: { type: Object, required: true }, // reactive Map<number, any[]>
  // The number of items in the list.
  nbHits: { type: Number, required: true, validator: val => Number.isInteger(val) && val >= 0 },
  // The number of items in a page from the item provider (e.g. a backend API).
  perPage: { type: Number, required: true, validator: val => Number.isInteger(val) && val >= 1 },
  bufferRatio: { type: Number, default: 2 },
  tag: { type: String, default: 'div' },
  probeTag: { type: String, default: 'div' },
  getKey: { type: Function, required: false, default: null },
  // Scroll to a specific item by index, must be less than the length prop
  scrollTo: { type: Number, required: false, validator: val => Number.isInteger(val) && val >= 0 },
  highlightDuration: { type: Number, default: 1200, validator: val => Number.isInteger(val) && val >= 0 }
})

const { getScrollTarget } = scroll

const rootRef = ref()
const probeRef = ref()
const resizeMeasurement = ref({})
const spaceBehindWindow = ref({ width: 0, height: 0 })
const keyPrefix = ref('')
const highlightedIndex = ref()

const { buffer, contentSize } = useReactiveBuffer({
  pages: computed(() => props.pages),
  nbHits: computed(() => props.nbHits),
  perPage: computed(() => props.perPage),
  resizeMeasurement,
  spaceBehindWindow,
  bufferRatio: computed(() => props.bufferRatio),
  onRevealPages: pages => emit('reveal-pages', pages)
})

const rootStyles = computed(() => {
  const styles = Object.fromEntries([
    ...Object.entries(contentSize.value ?? {}).map(([property, value]) => [
      property,
      value + 'px'
    ]),
    ['placeContent', 'start']
  ])
  return styles
})

const scrollToIndex = index => {
  const { x, y } = getItemOffsetByIndex(index, resizeMeasurement.value)
  const scrollTarget = getScrollTarget(rootRef.value)

  const targetX = x
  const targetY = y + resizeMeasurement.value.itemHeightWithGap

  highlightedIndex.value = index

  const scrollOptions = {
    scrollTarget, easing: 'easeOutSine',
    duration: 5000,
    left: targetX,
    top: targetY,
    onComplete: () => setTimeout(() => highlightedIndex.value = null, props.highlightDuration)
  }
  smoothScroll(scrollOptions)
}

watch(() => props.pages, () => {
  keyPrefix.value = String(new Date().getTime())
}, { immediate: true })

onMounted(() => {
  const scrollParents = getScrollParents(rootRef.value)

  const update = () => {
    if (!rootRef.value || !probeRef.value) return
    const rect = probeRef.value.getBoundingClientRect()
    resizeMeasurement.value = getResizeMeasurement(rootRef.value, {
      height: rect.height,
      width: rect.width
    })
    spaceBehindWindow.value = computeSpaceBehindWindowOf(rootRef.value)
  }

  // const onScroll = () => requestAnimationFrame(update)
  let scrollTimeout
  const onScroll = () => {
    requestAnimationFrame(update)
    clearTimeout(scrollTimeout)
    scrollTimeout = setTimeout(() => emit('scroll-ended'), 180)
  }

  scrollParents.vertical.addEventListener('scroll', onScroll, { passive: true })
  scrollParents.horizontal.addEventListener('scroll', onScroll, { passive: true })

  const observer = new ResizeObserver(() => update())
  observer.observe(rootRef.value)
  observer.observe(probeRef.value)

  update()
  scrollToIndex(props.scrollTo)
})

defineExpose({ scrollToIndex, rootRef })
</script>
